import {
  Apartment,
  CalendarToday,
  ContactPhone,
  Event,
  Home,
  Hotel,
  Info,
  LocalHospital,
  People,
  Person,
  PersonPin,
  Phone,
  Wc,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { APP_LINK } from "../../common/menu/menuItems/menuList";
import { fetchAdmission } from "../../redux/admissions/admissionsActions";
import { updateAttender } from "../../redux/Attender/attenderAction";

const ViewAdmissionsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const { id } = useParams();
  const patientState = useSelector(
    (state) => state?.admission?.fetchAdmission || {}
  );
  const isDataLoading = useSelector(
    (state) => state?.admission?.loading || false
  );
  const admissionData = patientState?.data?.data?.[0] || null;

  useEffect(() => {
    try {
      dispatch(fetchAdmission(id, { admission_status: "3" }));
    } catch (error) {
      console.error("Error in dispatch:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch patient data",
        severity: "error",
      });
    }
  }, [dispatch, id]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const patient = admissionData
    ? {
        admission_id: admissionData.id || "N/A",
        name: admissionData.Patient?.patient_name || "N/A",
        ip_number: admissionData.Patient?.ip_number || "N/A",
        wardName: admissionData.Ward?.ward_name || "N/A",
        wardId: admissionData.Ward?.id || "N/A",
        floorName: admissionData.Floor?.floor_name || "N/A",
        bedNumber: admissionData.Patient?.ip_number || "N/A",
        admissionDate: admissionData.admit_date || null,
        dischargeDate: admissionData.discharge_date || null,
        age: calculateAge(admissionData.Patient?.dob) || "N/A",
        ZoneName: admissionData?.Block?.zone_name || "N/A",
        gender:
          admissionData.Patient?.gender_type === "M"
            ? "Male"
            : admissionData.Patient?.gender_type === "F"
            ? "Female"
            : "Other",
        department: admissionData.Department?.department_name || "N/A",
        block: admissionData.Block?.block_name || "N/A",
        mobileNumber: admissionData.Patient?.phone || "N/A",
        status: getAdmissionStatusText(admissionData.admission_status || 0),
        block_id: admissionData.block_id || "N/A",
        ward_id: admissionData.ward_id || "N/A",
        floor_id: admissionData.floor_id || "N/A",
        wardType: admissionData.Ward?.ward_type || "N/A",
      }
    : {
        name: "Loading...",
        wardNumber: "Loading...",
        bedNumber: "Loading...",
        admissionDate: null,
        dischargeDate: null,
        age: "Loading...",
        gender: "Loading...",
        department: "Loading...",
        block: "Loading...",
        mobileNumber: "Loading...",
        status: "Loading...",
        block_id: "Loading...",
        ward_id: "Loading...",
        floor_id: "Loading...",
      };

  const attendants =
    admissionData?.Attenders?.map((attendant) => ({
      id: attendant.id || "N/A",
      name: attendant.attender_name || "N/A",
      relation: getRelationText(attendant.relation) || "N/A",
      address: attendant.address || "N/A",
      phoneNumber: attendant.phone || "N/A",
      status: attendant.attender_status || 1,
      createdDate: attendant.createdAt || "N/A",
      updatedDate: attendant.updatedAt || "N/A",
      patient_id: attendant.patient_id || admissionData.patient_id || "N/A",
      block_id: admissionData.block_id || "N/A",
      ward_id: admissionData.ward_id || "N/A",
      floor_id: admissionData.floor_id || "N/A",
    })) || [];

  const handleStatusToggle = async (attendantId, currentStatus) => {
    setSwitchLoading(true);
    const newStatus = currentStatus === 1 ? "3" : "1";

    try {
      // Await the dispatch of the updateAttender action
      await dispatch(
        updateAttender(attendantId, { attender_status: newStatus })
      );

      // Fetch the updated admission data
      await dispatch(fetchAdmission(id, { admission_status: "3" }));

      // Show success snackbar
      setSnackbar({
        open: true,
        message: `Attendant status updated to ${
          newStatus === "1" ? "Active" : "Inactive"
        }`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating attendant status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update attendant status",
        severity: "error",
      });
    } finally {
      setSwitchLoading(false);
    }
  };

  function calculateAge(dob) {
    if (!dob) return "N/A";
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age.toString();
    } catch (error) {
      console.error("Error calculating age:", error);
      return "N/A";
    }
  }

  function getRelationText(relationCode) {
    try {
      switch (Number(relationCode)) {
        case 1:
          return "Spouse";
        case 2:
          return "Parent";
        case 3:
          return "Child";
        case 4:
          return "Sibling";
        case 5:
          return "friend";
        case 6:
          return "other";
        default:
          return "Unspecified";
      }
    } catch (error) {
      console.error("Error getting relation text:", error);
      return "Unspecified";
    }
  }

  function getAdmissionStatusText(statusCode) {
    try {
      switch (Number(statusCode)) {
        case 1:
          return "Admitted";
        case 2:
          return "In Treatment";
        case 3:
          return "Discharged";
        case 4:
          return "Critical";
        default:
          return "Unknown";
      }
    } catch (error) {
      console.error("Error getting admission status text:", error);
      return "Unknown";
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  const getStatusColor = (status) => {
    if (typeof status === "number") {
      return status === 1 ? "success" : "error";
    }

    try {
      switch (status?.toLowerCase()) {
        case "admitted":
          return "primary";
        case "discharged":
          return "success";
        case "critical":
          return "error";
        case "stable":
        case "in treatment":
          return "info";
        case "active":
          return "success";
        case "inactive":
          return "error";
        default:
          return "default";
      }
    } catch (error) {
      console.error("Error getting status color:", error);
      return "default";
    }
  };

  const getStatusText = (statusNum) => {
    return statusNum === 1 ? "Active" : "Inactive";
  };
  const handlePrint = async (attendant) => {
    setIsLoading(true);
    setSnackbar({
      open: true,
      message: "Preparing ID card for printing...",
      severity: "info",
    });

    try {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        throw new Error(
          "Unable to open print window. Please check your popup blocker settings."
        );
      }
      const safeString = (value) => {
        if (value === null || value === undefined) return "";
        return String(value).replace(/'/g, "\\'");
      };
      const qrCodeData = {
        id: attendant.id,
        attender_name: attendant.name,
        phone: attendant.phoneNumber,
        ward_id: attendant.ward_id,
        patient_name: safeString(patient.name),
        ward_type: safeString(patient.wardType),
        admission_id: safeString(admissionData?.id || "N/A"),
        ward: safeString(patient.wardName),
      };

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>ID Card - ${safeString(attendant.name)}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            @page {
              size: 70mm 110mm;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 4px;
              width: 70mm;
              height: 110mm;
              /* Ubuntu-friendly font stack */
              font-family: Ubuntu, 'Noto Sans', 'Liberation Sans', Arial, sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              background-color: white;
            }
            * {
              box-sizing: border-box;
            }
            .container {
              width: 100%;
              height: 100%;
              position: relative;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .date {
              position: absolute;
              top: 3mm;
              right: 2mm;
              font-size: 10px;
              font-weight: bold;
              color: black;
            }
            .header {
            margin-top: 10mm;
              margin-bottom: 2mm;
              text-align: center;
              width: 100%;
            }
            .header p {
              margin: 0;
              padding: 0;
              font-weight: bold;
              color: black;
            }
            .hospital-name {
              font-size: 8px;
              margin-bottom: 4px !important;
            }
            .card-type {
              font-size: 9px;
            }
            .qr-container {
              width: 52mm; 
              height: 52mm;
              margin-bottom: 4mm;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .qr-container canvas {
              width: 200px !important;
              height: 180px !important;
            }
            .details {
              width: 100%;
              padding: 0 2mm;
            }
            .row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2mm;
              width: 100%;
            }
            .column {
              width: 48%;
            }
            .label {
              font-size: 9px;
              font-weight: bold;
              color: black;
              display: block;
            }
            .value {
              font-size: 10px;
              font-weight: bold;
              color: black;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: block;
            }
            /* CUPS-specific print optimization */
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .qr-container canvas, #qrcode canvas, #qrcode img {
                width: 200px !important;
                height: 180px !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <p class="hospital-name">அரசு மோகன் குமாரமங்கலம் மருத்துவக் கல்லூரி மருத்துவமனை சேலம் -1</p>
              <p class="hospital-name">
                பார்வையாளர் அடையாள அட்டை 
                (${new Date().toLocaleDateString("en-GB")}) 
                
                </span>
              </p>
              <p class="hospital-name">மண்டலம்:
                  (${safeString(patient.ZoneName)})
              </p>
              <p class="hospital-name">சேர்க்கை தேதி:
                  (${safeString(formatDate(patient.admissionDate))})
              </p>
            </div>
            <div class="qr-container">
              <div id="qrcode"></div>
            </div>
            
            <div class="details">
              <div class="row">
                <div class="column">
                  <span class="label">IP எண்:</span>
                  <span class="value">${safeString(patient.ip_number)}</span>
                </div>
                <div class="column">
                  <span class="label">பார்வையாளர்:</span>
                  <span class="value">${safeString(attendant.name)}</span>
                </div>
              </div>
              
              <div class="row">
                <div class="column">
                  <span class="label">நோயாளி பெயர்:</span>
                  <span class="value">${safeString(patient.name)}</span>
                </div>
                <div class="column">
                  <span class="label">வார்டு:</span>
                  <span class="value">${safeString(patient.wardName)}</span>
                </div>
              </div>
              
              <div class="row">
                <div class="column">
                  <span class="label">மொபைல்:</span>
                  <span class="value">${safeString(
                    attendant.phoneNumber
                  )}</span>
                </div>
                <div class="column">
                  <span class="label">பாலினம்:</span>
                  <span class="value">${safeString(patient.gender)}</span>
                </div>
              </div>
            </div>
          </div>
    
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
          <script>
            window.onload = function() {
              const dataToEncode = JSON.stringify(${JSON.stringify(
                qrCodeData
              )});
              
              try {
                const qrcode = new QRCode(document.getElementById("qrcode"), {
                  text: dataToEncode,
                  width: 200,
                  height: 180,
                  colorDark: "#000000",
                  colorLight: "#ffffff",
                  correctLevel: QRCode.CorrectLevel.L  
                });
                setTimeout(function() {
                  const qrImg = document.querySelector("#qrcode img");
                  if (qrImg) {
                    qrImg.style.width = "200px";
                    qrImg.style.height = "180px";
                  }
                  
                  const qrCanvas = document.querySelector("#qrcode canvas");
                  if (qrCanvas) {
                    qrCanvas.style.width = "200px";
                    qrCanvas.style.height = "180px";
                  }
                  setTimeout(function() {
                    console.log("Using CUPS printing system on Ubuntu");
                    window.print();
                    window.addEventListener('afterprint', function() {
                      window.close();
                    });
                    setTimeout(function() {
                      window.close();
                    }, 3000);
                  }, 1000);
                }, 1000);
              } catch (error) {
                console.error("First QR code generation attempt failed:", error);
                document.getElementById("qrcode").innerHTML = "";
                try {
                  const compactData = {
                    i: "${safeString(attendant.id)}",                   // id
                    n: "${safeString(attendant.name)}",                 // name
                    ph: "${safeString(attendant.phoneNumber)}",         // phone
                    pi: "${safeString(
                      attendant.patient_id
                    )}",          // patient_id
                    bi: "${safeString(
                      attendant.block_id
                    )}",            // block_id
                    wi: "${safeString(
                      attendant.ward_id
                    )}",             // ward_id
                    fi: "${safeString(
                      attendant.floor_id
                    )}",            // floor_id
                    s: ${attendant.status || 0},                       // status
                    a: "${safeString(
                      attendant.address
                    )}",              // address
                    pn: "${safeString(
                      patient.name
                    )}",                  // patient_name
                    wt: "${safeString(
                      patient.wardType
                    )}",              // ward_type
                    ai: "${safeString(
                      admissionData?.id || "N/A"
                    )}",    // admission_id
                    w: "${safeString(patient.wardNumber)}"              // ward
                  };
                  
                  const qrcode = new QRCode(document.getElementById("qrcode"), {
                    text: JSON.stringify(compactData),
                    width: 200,
                    height: 180,
                    colorDark: "#000000",
                    colorLight: "#ffffff",
                    correctLevel: QRCode.CorrectLevel.L
                  });
                  
                  setTimeout(function() {
                    window.print();
                    setTimeout(function() { window.close(); }, 3000);
                  }, 1000);
                } catch (error2) {
                  console.error("Second QR code generation attempt failed:", error2);
                  
                  // Ubuntu-specific error handling
                  document.getElementById("qrcode").innerHTML = 
                    "<div style='text-align:center;padding:10px;'>" +
                    "<p style='color:red;font-weight:bold;'>QR Code Error: Data too large</p>" +
                    "<p>ID: ${safeString(attendant.id)}<br>" +
                    "Name: ${safeString(attendant.name)}<br>" +
                    "Patient: ${safeString(patient.name)}<br>" +
                    "Ward: ${safeString(patient.wardName)}</p>" +
                    "<p style='font-size:9px;'>Ubuntu users: Please check your CUPS printer configuration</p>" +
                    "<button onclick='window.print()' style='padding:5px 10px;margin-top:10px;'>Print Anyway</button>" +
                    "</div>";
                }
              }
            };
          </script>
        </body>
        </html>
      `);

      printWindow.document.close();

      setSnackbar({
        open: true,
        message: "ID card sent to printer",
        severity: "success",
      });
    } catch (error) {
      console.error("Error printing ID card:", error);
      setSnackbar({
        open: true,
        message: "Error printing ID card: " + error.message,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(APP_LINK.LISTADMISSIONDETAILS);
  };

  function formatDateTime(dateTimeString) {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      const formattedDate = date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${formattedDate}, ${formattedTime}`;
    } catch (error) {
      console.error("Error formatting date and time:", error);
      return "N/A";
    }
  }
  return (
    <Box sx={{ maxWidth: "100vw", mx: "auto", p: 2 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={8} lg={9} xl={11}>
          <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
            Patient Details
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={3} xl={1} mb={2}>
          <Button
            variant="contained"
            color="error"
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Back
          </Button>
        </Grid>
      </Grid>

      {isDataLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          <Card elevation={3} sx={{ mb: 4, borderRadius: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h5"
                  component="div"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Person sx={{ verticalAlign: "middle", mr: 1 }} />
                  Patient Information
                </Typography>
                <Chip
                  label={patient.status}
                  color={getStatusColor(patient.status)}
                  icon={<Info />}
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Person sx={{ fontSize: "small", mr: 1 }} />
                    Name
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {patient.name}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Wc sx={{ fontSize: "small", mr: 1 }} />
                    Gender
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {patient.gender}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Hotel sx={{ fontSize: "small", mr: 1 }} />
                    Ward /IP No
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {patient.wardName} / {patient.bedNumber}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <LocalHospital sx={{ fontSize: "small", mr: 1 }} />
                    Department
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {patient.department}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Event sx={{ fontSize: "small", mr: 1 }} />
                    Admission Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(patient.admissionDate)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <CalendarToday sx={{ fontSize: "small", mr: 1 }} />
                    Floor
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {patient.floorName}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Apartment sx={{ fontSize: "small", mr: 1 }} />
                    Block
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {patient.block}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Phone sx={{ fontSize: "small", mr: 1 }} />
                    Mobile Number
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {patient.mobileNumber}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {attendants && attendants.length > 0 ? (
            attendants.map((attendant, index) => (
              <Card
                key={index}
                elevation={3}
                sx={{
                  mb: 4,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="div"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <PersonPin sx={{ verticalAlign: "middle", mr: 1 }} />
                      Attendant Information
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: { xs: 1, md: 0 },
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={attendant.status === 1}
                              onChange={() =>
                                handleStatusToggle(
                                  attendant.id,
                                  attendant.status
                                )
                              }
                              disabled={switchLoading}
                              color={
                                attendant.status === 1 ? "success" : "error"
                              }
                            />
                          }
                          label={
                            switchLoading ? (
                              <CircularProgress size={16} />
                            ) : attendant.status === 1 ? (
                              ""
                            ) : (
                              ""
                            )
                          }
                          labelPlacement="end"
                        />
                        <Chip
                          label={getStatusText(attendant.status)}
                          color={getStatusColor(attendant.status)}
                          icon={<Info />}
                          variant="outlined"
                        />
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handlePrint(attendant)}
                        startIcon={
                          isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <FileDownloadIcon />
                          )
                        }
                        disabled={isLoading}
                        sx={{ flexGrow: { xs: 1, md: 0 }, height: 40 }}
                      >
                        {isLoading ? "Generating..." : "Download ID Card"}
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Box marginBottom={3}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <PersonPin sx={{ fontSize: "small", mr: 1 }} />
                          Name
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {attendant.name}
                        </Typography>
                      </Box>
                      <Box marginBottom={3}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <People sx={{ fontSize: "small", mr: 1 }} />
                          Relation
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {attendant.relation}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Event sx={{ fontSize: "small", mr: 1 }} />
                          Created Date
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatDateTime(attendant.createdDate)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box marginBottom={3}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Home sx={{ fontSize: "small", mr: 1 }} />
                          Address
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {attendant.address}
                        </Typography>
                      </Box>
                      <Box marginBottom={3}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <ContactPhone sx={{ fontSize: "small", mr: 1 }} />
                          Phone Number
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {attendant.phoneNumber}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Event sx={{ fontSize: "small", mr: 1 }} />
                          Updated Date
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatDateTime(attendant.updatedDate)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          height: "100%",
                          minHeight: 150,
                        }}
                      >
                        <QRCodeCanvas
                          value={JSON.stringify({
                            id: attendant.id,
                            attender_name: attendant.name,
                            phone: attendant.phoneNumber,
                            patient_id: attendant.patient_id,
                            block_id: attendant.block_id,
                            ward_id: attendant.ward_id,
                            floor_id: attendant.floor_id,
                            status: attendant.status,
                            address: attendant.address,
                            patient_name: patient.name,
                            ward_type: patient.wardType,
                            admission_id: admissionData?.id || "N/A",
                            ward: patient.wardNumber,
                          })}
                          size={150}
                          level="H"
                          includeMargin={true}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card elevation={3} sx={{ mb: 4, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" align="center" sx={{ py: 4 }}>
                  No attendants registered for this patient
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default ViewAdmissionsList;
